import HeaderButton from '@AMIEWEB/Common/Buttons/HeaderButton';
import { HeaderContactDrawer } from '@AMIEWEB/Common/Drawer/HeaderContactDrawer';
import { HeaderContactDrawerContent, IFacilitySidePanelTabs } from '@AMIEWEB/Common/Drawer/HeaderContactDrawerContent';
import {
  SkillSetContent,
  getName,
} from '@AMIEWEB/Facility/FacilityDetails/FacilityHeader/HeaderUtils';
import { FacilityContactsActions } from '@AMIEWEB/Facility/FacilityDetails/FacilityTabs/ContactTab/Store/FacilityContacts.redux';
import { facilityActions } from '@AMIEWEB/Facility/FacilityStore/Facility.redux';
import { selectFacility } from '@AMIEWEB/Facility/FacilityStore/Facility.selector';
import {
  selectedOrderDetails,
  selectedOrderTypeDetails,
  selectedUnit,
  selectedUnitSkillSets,
} from '@AMIEWEB/NewOrder/Store/Order.selectors';
import { IHeaderRow } from '@AMIEWEB/NewOrder/types';
import { FormatDate } from '@AMIEWEB/Notification/Tasks/Common/FormatData';
import { selectOrderDetailsOrderType, selectOrderDetailsUIView } from '@AMIEWEB/Order/Store/Order.selectors';
import { useInformationalHeaderStyles } from '@AMIEWEB/Unit/Header/InformationalHeader.Styles';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { Avatar, Divider, Grid, Skeleton, Typography, useMediaQuery } from '@mui/material';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { CustomTooltip } from 'app/components/Common/Tooltips';
import { ResponsiveColumnContainer } from 'app/layout/ResposiveColumnContainer';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { theme } from 'styles/global-styles';
import { calculateOrderTypeDefaultValue, getFacilityHeaderAddressTemplate, missingField } from '../helper';
import { globalSearchActions } from 'store/redux-store/global-search/slice';
import { HeaderContactDrawerOrder } from '@AMIEWEB/Candidate/CandidateProfile/CandidateHeader/HeaderContactDrawer';
import { getAge } from '@AMIEWEB/Order/OrderDetails/helper';
import { ISkillSet, ISkillSetInfo } from '@AMIEWEB/Facility/FacilityStore/Facility.model';
import { Concatenate } from 'utils/string/string';
import { usePromiseTracker } from 'react-promise-tracker';

const HeaderColumn = ({ rowData, classes }) => {
  const showTooltip = useMediaQuery('(min-width: 1228px)');
  return (
    <Grid container className={classes.fieldSection} direction="column">
      {rowData.map((item, index) => (
        <Grid container className={classes.orderHeaderFieldContainer} data-testid="column-id" id={item.id} key={index}>
          <Grid item className={classes.orderFieldHead} data-id="content">
            <div>{item.rowHead}</div>
          </Grid>
          <Grid item className={classes.orderHeaderfieldContentContainer} data-id="content">
            {item.rowContent !== missingField ? (
              <CustomTooltip hideToolTip={showTooltip} placement="bottom" arrow={true} tooltipContent={item.rowContent}>
                <div className={classes.fieldContent}>{item.rowContent}</div>
              </CustomTooltip>
            ) : (
              <div className={classes.fieldContent}>{item.rowContent}</div>
            )}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};

interface OrderDetailHeaderProps {
  fetchingOrderDetails?: boolean;
}

const OrderDetailHeader: React.FC<OrderDetailHeaderProps> = ({ fetchingOrderDetails = false }) => {
  const { classes } = useInformationalHeaderStyles();
  const { t } = useTranslation();
  const { promiseInProgress: fetchingFacilityData } = usePromiseTracker({ area: 'facility', delay: 0 });
  const [openProfileDrawer, setOpenProfileDrawer] = useState<boolean>(false);
  const history = useNavigate();
  const facilityData = useSelector(selectFacility);
  const selectedUnitItem = useSelector(selectedUnit);
  const selectUnitSkillSets = useSelector(selectedUnitSkillSets);
  const selectOrderDetailOrderType = useSelector(selectOrderDetailsOrderType);
  const orderDetailsData = useSelector(selectedOrderTypeDetails);
  const orderDetails = useSelector(selectedOrderDetails);
  const orderDetailsUIView = useSelector(selectOrderDetailsUIView);
  const ordDetails = orderDetailsUIView?.orderDetails ?? orderDetails;

  const facilityResponse = facilityData?.facilityResponse;
  const { id: facilityId } = useParams<{ id: string }>();

  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isNewOrder = pathname.includes('order/new');
  const facilityName = facilityResponse?.facilityName ?? missingField;
  const isDesktopSize = useMediaQuery('(min-width: 1920px)');
  const skillSets: ISkillSetInfo[] = [];
  
  if (!isNewOrder) {
    ordDetails?.skillSetGroup?.groups?.forEach(g => {
      g?.skillsets.forEach(ss => {
        const skillSet = { ...ss.skillSet };
        skillSet.discipline = ss.skillSet?.disciplineAbbr;
        skillSet.specialty = ss.skillSet?.specialty;
        if (ss.skillSet.subSpecialty) {
          skillSet.specialty = Concatenate([skillSet.specialty, ss.skillSet?.subSpecialtyAbbr], '-');
        }
        if (skillSet) skillSets.push(skillSet);
      });
    });
  } else {
    selectUnitSkillSets?.groups?.forEach(g => {
      g?.skillsets.forEach((ss: ISkillSet) => {
        const skillSet = { ...ss.skillSet };
        skillSet.discipline = ss.skillSet.discipline?.object?.Abbreviation;
        skillSet.specialty = ss.skillSet?.specialty?.label ?? null;
        if (ss.skillSet.subSpecialty) {
          skillSet.specialty = Concatenate([skillSet.specialty, ss.skillSet.subSpecialty?.object?.Description], '-');
        }
        if (skillSet && skillSet?.discipline) skillSets.push(skillSet);
      });
    });
  }

  useEffect(() => {
    const id = facilityResponse?.facilityId ?? +facilityId;
    if (isNewOrder && id && !facilityResponse) {
      dispatch(
        FacilityContactsActions.requestFacilityClientContactsList({
          id: id,
        }),
      );

      dispatch(facilityActions.getFacilityDetailsWithOpenOrderCountAction({ id: id }));
    }
  }, [facilityResponse]);

  const headerRowData: IHeaderRow[] = [
    {
      rowHead: t('facility.orderHeader.orderAge'),
      rowContent: fetchingOrderDetails ? (
        <Skeleton variant="rounded" width={230} height={15} />
      ) : !isNewOrder ? (
        getAge(ordDetails?.dateCreated)
      ) : (
        missingField
      ),
      id: 'orderAge',
    },
    {
      rowHead: t('facility.orderHeader.verifiedOn'),
      rowContent: fetchingOrderDetails ? (
        <Skeleton variant="rounded" width={230} height={15} />
      ) : ordDetails?.dateVerified ? (
        FormatDate(ordDetails?.dateVerified, 'MM/DD/YYYY')
      ) : (
        missingField
      ),
      id: 'verfiedOn',
      type: 'verifiedOn',
    },
    {
      rowHead: t('facility.orderHeader.verifiedBy'),
      rowContent: fetchingOrderDetails ? (
        <Skeleton variant="rounded" width={230} height={15} />
      ) : (
        getName(ordDetails?.contacts?.verifiedBy) || missingField
      ),
      id: 'verifiedBy',
    },
    {
      rowHead: (
        <CustomTooltip tooltipContent={t('order.orderDetails.orderHeader.divisionTooltipText')}>
          <BusinessOutlinedIcon sx={{ color: theme.palette.system.buttonDarkGray }} />
        </CustomTooltip>
      ),
      rowContent:
        fetchingFacilityData || fetchingOrderDetails ? (
          <Skeleton variant="rounded" width={230} height={15} />
        ) : !isNewOrder ? (
          ordDetails?.division
        ) : (
          facilityResponse?.division || missingField
        ),
      id: 'unitHeaderAMNDivision',
    },
    {
      rowHead: (
        <CustomTooltip tooltipContent={t('facility.orderHeader.orderType')}>
          <AccountTreeOutlinedIcon sx={{ color: theme.palette.system.buttonDarkGray }} />
        </CustomTooltip>
      ),
      rowContent:
        fetchingFacilityData || fetchingOrderDetails ? (
          <Skeleton variant="rounded" width={230} height={15} />
        ) : !isNewOrder ? (
          ordDetails?.orderType
        ) : (
          orderDetailsData?.orderType ||
          calculateOrderTypeDefaultValue(selectOrderDetailOrderType, facilityData)?.Description ||
          missingField
        ),
      id: 'unitHeaderHealthcareSystem',
    },
    {
      rowHead: (
        <CustomTooltip tooltipContent={t('facility.orderHeader.businessRelationship')}>
          <HandshakeOutlinedIcon sx={{ color: theme.palette.system.buttonDarkGray }} />
        </CustomTooltip>
      ),
      rowContent:
        fetchingFacilityData || fetchingOrderDetails ? (
          <Skeleton variant="rounded" width={230} height={15} />
        ) : !isNewOrder ? (
          ordDetails?.facility.businessRelationship ?? missingField
        ) : (
          facilityResponse?.businessRelationship ?? missingField
        ),
      id: 'unitHeaderBusinessRelationship',
    },
    {
      rowHead: (
        <CustomTooltip tooltipContent={t('facility.orderHeader.technologyVendor')}>
          <ApiOutlinedIcon sx={{ color: theme.palette.system.buttonDarkGray }} />
        </CustomTooltip>
      ),
      rowContent:
        fetchingFacilityData || fetchingOrderDetails ? (
          <Skeleton variant="rounded" width={230} height={15} />
        ) : !isNewOrder ? (
          ordDetails?.technologyVendor || missingField
        ) : (
          facilityResponse?.technologyVendor || missingField
        ),
      id: 'unitHeaderTechnologyVendor',
    },

    {
      rowHead: t('facility.facilityHeader.phone'),
      rowContent: missingField,
      id: 'unitHeaderPhone',
      type: 'phone',
    },
    {
      rowHead: (
        <CustomTooltip tooltipContent={t('facility.orderHeader.accountManager')}>
          <Avatar
            sx={theme => ({
              fontWeight: '400',
              width: '24px',
              height: '24px',
              background: theme.palette.system.buttonDarkGray,
              color: theme.palette.components.avatar.color,
              fontSize: '12px !important',
            })}
          >
            {t('facility.orderHeader.am')}
          </Avatar>
        </CustomTooltip>
      ),
      rowContent:
        fetchingFacilityData || fetchingOrderDetails ? (
          <Skeleton variant="rounded" width={230} height={15} />
        ) : !isNewOrder ? (
          getName(ordDetails?.contacts?.accountManager)
        ) : (
          getName(facilityResponse?.employeeRoles?.accountManager)
        ),

      id: 'unitHeaderAccountManager',
    },
    {
      rowHead: (
        <CustomTooltip tooltipContent={t('facility.orderHeader.regionalDirector')}>
          <Avatar
            sx={theme => ({
              fontWeight: '400',
              width: '24px',
              height: '24px',
              background: theme.palette.system.buttonDarkGray,
              color: theme.palette.components.avatar.color,
              fontSize: '12px !important',
            })}
          >
            {t('facility.orderHeader.rd')}
          </Avatar>
        </CustomTooltip>
      ),
      rowContent:
        fetchingFacilityData || fetchingOrderDetails ? (
          <Skeleton variant="rounded" width={230} height={15} />
        ) : !isNewOrder ? (
          getName(ordDetails?.contacts?.regionalDirector)
        ) : (
          getName(facilityResponse?.employeeRoles?.regionalDirector)
        ),

      id: 'unitHeaderRegionalDirector',
    },
    {
      rowHead: t('facility.orderHeader.createdOn'),
      rowContent: fetchingOrderDetails ? (
        <Skeleton variant="rounded" width={230} height={15} />
      ) : ordDetails?.dateCreated ? (
        FormatDate(ordDetails?.dateCreated, 'MM/DD/YYYY')
      ) : (
        missingField
      ),
      id: 'createdOn',
    },
    {
      rowHead: t('facility.orderHeader.createdBy'),
      rowContent: fetchingOrderDetails ? (
        <Skeleton variant="rounded" width={230} height={15} />
      ) : (
        getName(ordDetails?.contacts?.createdBy)
      ),
      id: 'createdBy',
    },
  ];

  const openFacilityContanctDrawer = () => {
    if (isNewOrder) {
      dispatch(globalActions.resetFacilityDrawerData());
      dispatch(
        facilityActions.setFacilityDrawerVisibility({ open: true, tabId: IFacilitySidePanelTabs.CONTACTS }),
      );
    } else {
      dispatch(globalSearchActions.setDrawerData({ open: false, data: undefined }));
      dispatch(globalActions.resetAllRecentHistoryDrawers());
      setOpenProfileDrawer(true);
      dispatch(globalActions.setDetailsPageDrawer({ open: true }));
    }
  };

  const openFacilityDrawer = () => {
    dispatch(globalActions.resetAllRecentHistoryDrawers());
    dispatch(globalActions.resetFacilityDrawerData());
    const selectedFacilityId = !isNewOrder ? ordDetails?.facility?.facilityId : facilityResponse?.facilityId;
    const payload = { open: true, facility: { facilityId: selectedFacilityId } };
    dispatch(globalActions.setFacilityDrawerData(payload));
    dispatch(facilityActions.setFacilityDrawerVisibility({ open: false, tabId: 0 }));
  };

  const redirectToUnit = () => {
    const selectedFacilityId = !isNewOrder ? ordDetails?.facility?.facilityId : facilityResponse?.facilityId;
    const selectedUnitId = !isNewOrder ? ordDetails?.unit?.unitId : selectedUnitItem?.unitId;
    if (selectedUnitId) {
      history(`/facility/${selectedFacilityId}/unit/${selectedUnitId}`);
    }
  };

  const closeProfileDrawer = () => {
    if (isNewOrder) {
      dispatch(facilityActions.setFacilityDrawerVisibility({ open: false, tabId: 0 }));
    } else {
      setOpenProfileDrawer(false);
      dispatch(globalActions.setDetailsPageDrawer({ open: false }));
    }
  };

  return (
    <>
      <Grid
        data-testid="header-id"
        container
        xs={12}
        direction="row"
        classes={{ container: classes.orderHeaderContainer }}
      >
        <Grid item direction="column" classes={{ root: classes.containerAvatar }}>
          <Grid item direction="row" classes={{ root: classes.myAvatarAlignment }}>
            <HeaderButton
              headerAbbr=""
              headerAbbrBackground={isNewOrder ? theme.palette.system.violet : theme.palette.system.mutedVoilet}
              headerId={`OID ${!isNewOrder ? ordDetails?.orderId : missingField}`}
              customSearchType={`order`}
              headerIdBackground={isNewOrder ? theme.palette.system.mutedVoilet : theme.palette.system.violet}
              isDisabled={isNewOrder}
            />
          </Grid>
          <Grid container mt={2}>
            <Grid
              item
              className={classes.OrderCircleContainer}
              onClick={() => {
                openFacilityContanctDrawer();
              }}
            >
              <PersonOutlinedIcon sx={{ color: theme.palette.primary.main }} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item direction="column" classes={{ root: classes.orderDetailsContainerWrap }}>
          <Grid item container direction="row">
            <Grid item xs={4} style={{ minWidth: '170px' }}>
              <CustomTooltip
                tooltipContent={!isNewOrder ? ordDetails?.facility?.name : facilityName}
                hideToolTip={
                  isNewOrder
                    ? facilityName.length < (isDesktopSize ? 90 : 75)
                    : ordDetails?.facility?.name?.length < (isDesktopSize ? 90 : 75)
                }
              >
                <Typography mt={1} className={classes.orderHeaderFaciltyName} onClick={openFacilityDrawer}>
                  {fetchingFacilityData ? (
                    <Skeleton variant="rounded" width={230} height={15} />
                  ) : !isNewOrder ? (
                    ordDetails?.facility?.name?.length > (isDesktopSize ? 90 : 75) ? (
                      `${ordDetails?.facility?.name?.slice(0, isDesktopSize ? 90 : 75)}...`
                    ) : (
                      ordDetails?.facility?.name
                    )
                  ) : facilityName?.length > (isDesktopSize ? 90 : 75) ? (
                    `${facilityName?.slice(0, isDesktopSize ? 90 : 75)}...`
                  ) : (
                    facilityName
                  )}
                </Typography>
              </CustomTooltip>
              <CustomTooltip
                tooltipContent={!isNewOrder ? ordDetails?.unit?.name : selectedUnitItem?.name}
                hideToolTip={
                  isNewOrder
                    ? !!!selectedUnitItem || selectedUnitItem?.name?.length < (isDesktopSize ? 150 : 113)
                    : ordDetails?.unit?.name?.length < (isDesktopSize ? 150 : 113)
                }
              >
                <Typography className={classes.orderHeaderUnitName} onClick={redirectToUnit}>
                  {!isNewOrder ? ordDetails?.unit?.name ?? missingField : selectedUnitItem?.name ?? missingField}
                </Typography>
              </CustomTooltip>
            </Grid>

            <Divider orientation="vertical" flexItem classes={{ root: classes.orderDivider }} />
            <Grid item xs={2}>
              <HeaderColumn rowData={headerRowData.slice(3, 7)} classes={classes} />
            </Grid>
            <Divider orientation="vertical" flexItem classes={{ root: classes.orderDivider }} />
            <Grid item xs={2}>
              <HeaderColumn rowData={headerRowData.slice(8, 10)} classes={classes} />
              <HeaderColumn rowData={headerRowData.slice(10, 12)} classes={classes} />
            </Grid>
            <Divider orientation="vertical" flexItem classes={{ root: classes.orderDivider }} />
            <Grid item xs={2}>
              <HeaderColumn rowData={headerRowData.slice(0, 3)} classes={classes} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item direction="row" classes={{ root: classes.orderActionContainer }}>
        <Grid item sx={{ display: 'flex' }}>
          <ResponsiveColumnContainer
            id={'facilityHeaderAddress'}
            rowHead={t('facility.orderHeader.fidAddressOrder')}
            rowContent={
              <Typography variant="body1" className={classes.blueBarText}>
                {!isNewOrder
                  ? getFacilityHeaderAddressTemplate(ordDetails?.location)
                  : getFacilityHeaderAddressTemplate(facilityResponse?.location)}
              </Typography>
            }
            classes={classes}
            customRowHeadClass={classes.orderHeaderRowCustom}
            customRowContentClass={`${classes.orderRowContentCustom} ${classes.orderRowContentDefaultColor}`}
          />
        </Grid>
        <Grid item sx={{ display: 'flex' }}>
          <ResponsiveColumnContainer
            id={'unitHeaderAddress'}
            rowHead={t('facility.orderHeader.skillSetOrder')}
            rowContent={
              <SkillSetContent
                skillSets={!isNewOrder ? (!skillSets.length ? null : skillSets) : !skillSets.length ? null : skillSets}
                isOrderHeader={true}
              />
            }
            classes={classes}
            customRowHeadClass={classes.orderHeaderRowCustom}
            customRowContentClass={`${classes.orderRowContentCustom} ${classes.orderRowContentDefaultColor}`}
          />
        </Grid>
      </Grid>
      {isNewOrder && facilityData?.facilityDrawerState?.open && (
        <HeaderContactDrawer
          closeProfileDrawer={closeProfileDrawer}
          openProfileDrawer={facilityData?.facilityDrawerState?.open}
          isBorderAvatar={false}
          avatarOffSet={`FID`}
          avatarText={`${facilityResponse?.facilityId}`}
          title={facilityResponse?.facilityName}
          phone={facilityResponse?.phone ?? 'N/A'}
          children={<HeaderContactDrawerContent />}
        />
      )}
      {!isNewOrder && (
        <HeaderContactDrawerOrder
          closeProfileDrawer={closeProfileDrawer}
          openProfileDrawer={openProfileDrawer}
          avatarText={`${ordDetails?.orderId}`}
          candidateName={orderDetailsUIView?.headerDetails?.facilityName}
          orderDetails={orderDetailsUIView?.headerDetails}
        />
      )}
    </>
  );
};

export default OrderDetailHeader;
